/**
 * Exercise lifecycle state machine.
 *
 * This is the authoritative state machine for tutorial exercise scenes.
 * It coordinates the lifecycle between React (video timing events) and
 * Phaser (scene initialization, graphics, cursor, metronome).
 *
 * The machine structurally prevents the race condition where
 * StartExercise fires before UpdateObjective has been processed:
 *   - START_EXERCISE in `idle` is a no-op (silently dropped).
 *   - START_EXERCISE during `loading` transitions to `waitingForStart`,
 *     which auto-advances to `waitingForInput` when OBJECTIVE_LOADED
 *     arrives.
 *
 * See `exerciseMachine.types.ts` for type definitions and
 * `IExerciseSceneFacade.ts` for the scene interface.
 */

import { setup, assign } from 'xstate';
import type { ExerciseContext, ExerciseEvent, ExerciseMachineInput } from './exerciseMachine.types';

export const SIDE_EFFECT_ACTION_NAMES = [
  'loadObjective',
  'unloadObjective',
  'initExercise',
  'beginPlayback',
  'stopPlayback',
  'evaluatePerformance',
  'notifyShowExercise',
  'notifyHideExercise',
  'notifyExercisePassed',
  'notifyObjectiveComplete',
  'notifyObjectiveSkipped',
  'requestSkipButton',
  'showPassAnimation',
  'showFailAnimation',
  'pauseScene',
  'resumeScene',
  'cleanup',
  'unloadGraphics',
  'playNarration',
  'scheduleStop',
] as const;

export type SideEffectActionName = (typeof SIDE_EFFECT_ACTION_NAMES)[number];

const defaultContext: ExerciseContext = {
  objectiveIndex: undefined,
  totalObjectives: 0,
  chapterIdx: undefined,
  paused: false,
  failedAttempts: 0,
  skipped: false,
};

function buildStubSideEffects(): Record<SideEffectActionName, () => void> {
  const map = {} as Record<SideEffectActionName, () => void>;
  for (const name of SIDE_EFFECT_ACTION_NAMES) {
    map[name] = () => {
      // eslint-disable-next-line no-console -- intentional stub logging
      console.log(`[EXERCISE-MACHINE-STUB] action: ${name}`);
    };
  }
  return map;
}

export const exerciseMachine = setup({
  types: {
    context: {} as ExerciseContext,
    events: {} as ExerciseEvent,
    input: {} as ExerciseMachineInput | undefined,
  },
  guards: {
    isCorrectChapter: ({ context, event }) =>
      event.type === 'UPDATE_OBJECTIVE' && event.chapter === context.chapterIdx,
    isLastObjective: ({ context }) =>
      context.objectiveIndex !== undefined && context.objectiveIndex >= context.totalObjectives - 1,
    isNotLastObjective: ({ context }) =>
      context.objectiveIndex !== undefined && context.objectiveIndex < context.totalObjectives - 1,
    isNotPaused: ({ context }) => !context.paused,
  },
  actions: {
    ...buildStubSideEffects(),

    incrementObjective: assign({
      objectiveIndex: ({ context }) =>
        context.objectiveIndex !== undefined ? context.objectiveIndex + 1 : 0,
    }),
    resetForNewObjective: assign({
      failedAttempts: () => 0,
    }),
    resetContext: assign({
      objectiveIndex: () => undefined as number | undefined,
      paused: () => false,
      failedAttempts: () => 0,
      skipped: () => false,
    }),
    setPausedTrue: assign({ paused: () => true }),
    setPausedFalse: assign({ paused: () => false }),
    incrementFailedAttempts: assign({
      failedAttempts: ({ context }) => context.failedAttempts + 1,
    }),
    setSkippedTrue: assign({ skipped: () => true }),
  },
}).createMachine({
  id: 'exerciseLifecycle',
  initial: 'idle',
  context: ({ input }) => ({
    ...defaultContext,
    ...(input ?? {}),
  }),

  on: {
    UNLOAD_EXERCISE: {
      target: '.idle',
      actions: ['cleanup', 'notifyHideExercise', 'resetContext'],
    },
    PAUSE_EXERCISE: {
      actions: ['setPausedTrue', 'pauseScene'],
    },
    RESUME_EXERCISE: {
      actions: ['setPausedFalse', 'resumeScene'],
    },
  },

  states: {
    // -----------------------------------------------------------------
    idle: {
      on: {
        UPDATE_OBJECTIVE: {
          target: 'loading',
          guard: 'isCorrectChapter',
          actions: 'incrementObjective',
        },
        // START_EXERCISE arriving before any objective has loaded is
        // silently dropped. This is the primary race condition fix.
        START_EXERCISE: {},
      },
    },

    // -----------------------------------------------------------------
    loading: {
      entry: ['notifyShowExercise', 'loadObjective'],
      on: {
        OBJECTIVE_LOADED: 'objectiveReady',
        // START_EXERCISE arriving while the objective is still loading
        // transitions to waitingForStart, which defers the start until
        // OBJECTIVE_LOADED arrives.
        START_EXERCISE: 'waitingForStart',
        // NARRATION_COMPLETE can arrive during loading if narration is
        // shorter than graphics build time. Defer it like START_EXERCISE.
        NARRATION_COMPLETE: 'waitingForStart',
        UPDATE_OBJECTIVE: {},
      },
    },

    // -----------------------------------------------------------------
    // START_EXERCISE arrived before OBJECTIVE_LOADED. Wait for the load
    // to complete, then auto-advance to waitingForInput.
    waitingForStart: {
      on: {
        OBJECTIVE_LOADED: {
          target: 'waitingForInput',
          actions: ['resetForNewObjective'],
        },
        UPDATE_OBJECTIVE: {},
      },
    },

    // -----------------------------------------------------------------
    // Objective graphics are loaded, exercise overlay is visible.
    // Video is still playing the intro narration. When the video
    // reaches StartExercise, it pauses and the exercise becomes
    // interactive. For auto-advanced objectives (no video),
    // NARRATION_COMPLETE fires after the native narration finishes.
    objectiveReady: {
      on: {
        START_EXERCISE: {
          target: 'waitingForInput',
          actions: 'resetForNewObjective',
        },
        NARRATION_COMPLETE: {
          target: 'waitingForInput',
          actions: 'resetForNewObjective',
        },
      },
    },

    // -----------------------------------------------------------------
    waitingForInput: {
      entry: ['initExercise'],
      on: {
        NOTE_PLAYED: {
          target: 'playing',
          guard: 'isNotPaused',
        },
        // Piano exercises complete immediately on correct key press
        // without going through the playing state.
        PHRASE_END: 'evaluating',
        SKIP_EXERCISE: {
          target: 'skipping',
          actions: 'setSkippedTrue',
        },
      },
    },

    // -----------------------------------------------------------------
    playing: {
      entry: ['beginPlayback'],
      on: {
        PHRASE_END: 'evaluating',
        SKIP_EXERCISE: {
          target: 'skipping',
          actions: 'setSkippedTrue',
        },
      },
    },

    // -----------------------------------------------------------------
    evaluating: {
      entry: ['stopPlayback', 'evaluatePerformance'],
      on: {
        OBJECTIVE_PASSED: 'passed',
        OBJECTIVE_FAILED: 'failed',
      },
    },

    // -----------------------------------------------------------------
    passed: {
      entry: ['showPassAnimation'],
      on: {
        TRANSITION_COMPLETE: [
          {
            target: 'exerciseComplete',
            guard: 'isLastObjective',
            actions: ['notifyExercisePassed', 'notifyObjectiveComplete'],
          },
          {
            target: 'advancingObjective',
            guard: 'isNotLastObjective',
          },
        ],
      },
    },

    // -----------------------------------------------------------------
    advancingObjective: {
      entry: ['unloadGraphics'],
      after: {
        800: {
          target: 'loading',
          actions: ['incrementObjective', 'playNarration'],
        },
      },
    },

    // -----------------------------------------------------------------
    failed: {
      entry: ['incrementFailedAttempts', 'showFailAnimation', 'requestSkipButton'],
      on: {
        RETRY: 'waitingForInput',
        SKIP_EXERCISE: {
          target: 'skipping',
          actions: 'setSkippedTrue',
        },
      },
    },

    // -----------------------------------------------------------------
    skipping: {
      entry: ['notifyObjectiveSkipped'],
      always: [
        {
          target: 'exerciseComplete',
          guard: 'isLastObjective',
        },
        {
          target: 'advancingObjective',
          guard: 'isNotLastObjective',
        },
      ],
    },

    // -----------------------------------------------------------------
    exerciseComplete: {
      entry: ['scheduleStop'],
      type: 'final',
    },
  },
});

export type ExerciseMachine = typeof exerciseMachine;
